import React from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import {
    CheckCircleFill,
    ExclamationTriangleFill,
    ShieldLockFill,
} from 'react-bootstrap-icons';
import './BillingPaymentProgressModal.css';

export type BillingPaymentProgressState = 'pending' | 'verifying' | 'processing' | 'succeeded' | 'failed';

export interface BillingPaymentProgressStep
{
    id: string;
    label: string;
    state: BillingPaymentProgressState;
    detail: string;
}

interface BillingPaymentProgressModalProps
{
    show: boolean;
    steps: BillingPaymentProgressStep[];
    onHide: () => void;
}

function isInProgress(steps: BillingPaymentProgressStep[]): boolean
{
    return steps.some((step) => !['succeeded', 'failed'].includes(step.state));
}

function getStepStatusLabel(state: BillingPaymentProgressState): string
{
    if (state === 'succeeded') return 'Completed';
    if (state === 'failed') return 'Needs attention';
    if (state === 'verifying') return 'Verification required';
    if (state === 'pending') return 'Waiting';
    return 'Processing';
}

export default function BillingPaymentProgressModal({
    show,
    steps,
    onHide,
}: BillingPaymentProgressModalProps): React.ReactElement
{
    const inProgress = isInProgress(steps);

    return (
        <Modal
            dialogClassName="bppm-modal"
            show={show}
            onHide={() =>
            {
                if (!inProgress) onHide();
            }}
            backdrop={inProgress ? 'static' : true}
            keyboard={!inProgress}
            centered
        >
            <Modal.Header closeButton={!inProgress} className="bppm-header">
                <div className="bppm-title-wrap">
                    <span className="bppm-title-icon" aria-hidden="true">
                        <ShieldLockFill size={18} />
                    </span>
                    <div>
                        <Modal.Title>Secure payment</Modal.Title>
                        <p className="bppm-title-subtitle">{inProgress ? 'Completing your billing request' : 'Payment status'}</p>
                    </div>
                </div>
            </Modal.Header>
            <Modal.Body className="bppm-body">
                <p className="bppm-intro">
                    {inProgress
                        ? 'Keep this page open while Stripe completes each payment step.'
                        : 'Review the payment status below.'}
                </p>
                <div className="bppm-steps">
                    {steps.map((step) => (
                        <div key={step.id} className={`bppm-step bppm-step-${step.state}`}>
                            <span className="bppm-step-indicator" aria-hidden="true">
                                {step.state === 'succeeded' ? (
                                    <CheckCircleFill size={18} />
                                ) : step.state === 'failed' ? (
                                    <ExclamationTriangleFill size={18} />
                                ) : step.state === 'verifying' ? (
                                    <ShieldLockFill size={18} />
                                ) : (
                                    <Spinner animation="border" size="sm" />
                                )}
                            </span>
                            <div className="bppm-step-copy">
                                <div className="bppm-step-heading">
                                    <span className="bppm-step-label">{step.label}</span>
                                    <span className="bppm-step-status">{getStepStatusLabel(step.state)}</span>
                                </div>
                                <p className="bppm-step-detail">{step.detail}</p>
                            </div>
                            {step.state === 'verifying' ? (
                                <span className="visually-hidden">Card verification window is open.</span>
                            ) : (
                                null
                            )}
                        </div>
                    ))}
                </div>
            </Modal.Body>
            {!inProgress && (
                <Modal.Footer className="bppm-footer">
                    <button type="button" className="btn btn-primary bppm-close-button" onClick={onHide}>
                        Close
                    </button>
                </Modal.Footer>
            )}
        </Modal>
    );
}
